import { useState, useEffect } from 'react';

/**
 * Create a countdown timer with given duration in milliseconds
 * with setDuration and start functionality and return hours, minutes, seconds in 2 digits
 * @returns {object} { time, duration, setDuration, start, pause, resume, reset }
 */
export default function useCountdown(initialDuration = 0) {
    const [duration, setDurationState] = useState(
        Number.isFinite(initialDuration) && initialDuration > 0 ? initialDuration : 0
    );
    const [time, setTime] = useState({ hours: '00', minutes: '00', seconds: '00' });
    const [isPaused, setIsPaused] = useState(true);

    const setDuration = (newDuration) => {
        setDurationState(prev => {
            const resolved = typeof newDuration === 'function' ? newDuration(prev) : newDuration;
            const valid = Number.isFinite(resolved) ? Math.max(0, resolved) : 0;
            return valid;
        });
    };

    const start = () => {
        setIsPaused(false);
    };

    const pause = () => {
        setIsPaused(true);
    };

    const resume = () => {
        setIsPaused(false);
    };

    const reset = () => {
        setIsPaused(true);
        setDurationState(0);
        setTime({ hours: '00', minutes: '00', seconds: '00' });
    };

    useEffect(() => {
        if (isPaused) return;

        const interval = setInterval(() => {
            setDurationState(prev => {
                if (prev <= 1000) {
                    setIsPaused(true);
                    return 0;
                }
                return prev - 1000;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isPaused]);

    useEffect(() => {
        const validDuration = Number.isFinite(duration) ? Math.max(0, duration) : 0;
        const seconds = Math.floor((validDuration / 1000) % 60);
        const minutes = Math.floor((validDuration / (1000 * 60)) % 60);
        const hours = Math.floor((validDuration / (1000 * 60 * 60)) % 24);

        setTime({
            hours: hours < 10 ? `0${hours}` : `${hours}`,
            minutes: minutes < 10 ? `0${minutes}` : `${minutes}`,
            seconds: seconds < 10 ? `0${seconds}` : `${seconds}`
        });
    }, [duration]);

    return { time, duration, setDuration, start, pause, resume, reset };
}